// Shared queue/escalation logic — used by Overview's summary cards and by the three
// dedicated pages those cards link to. Overview scopes counts by workshop + created-date
// range; detail pages read the same filters from ?location=&from=&to= query params.

import { isEscalationFlagged } from "./journey";

export function isSent(val) {
  return String(val || "").trim().length > 0;
}

// Stages checked against approval / feedback columns returned by backend
export const APPROVAL_STAGES = [
  { sentCol: "Pre Invoice Sent", ackCol: "Pre Invoice Approval", label: "Pre Invoice" },
  { sentCol: "Invoice Sent", ackCol: "Invoice Approval", label: "Invoice" },
  { sentCol: "Parts Approval Sent", ackCol: "Parts Approval Notes", label: "Parts Approval" },
  { sentCol: "Feedback Notified", ackCol: "Feedback Link", label: "Feedback" },
];

export function pendingApprovalStage(row) {
  return APPROVAL_STAGES.find((s) => isSent(row[s.sentCol]) && !isSent(row[s.ackCol])) || null;
}

export const OUTBOUND_SENT_COLS = ["Estimate Sheet Sent", "Job Card Sent", "Work Progress Sent", "Media Sent", "Delivery Media Sent"];

export function isTerminalStatus(status) {
  return ["delivered", "closed", "completed"].includes(String(status || "").trim().toLowerCase());
}

export function isPendingResponse(row) {
  const anySent = OUTBOUND_SENT_COLS.some((col) => isSent(row[col]));
  const alreadyApproval = pendingApprovalStage(row) !== null;
  return anySent && !alreadyApproval && !isTerminalStatus(row["Status"]);
}

export function minsSince(iso) {
  if (!iso) return 0;
  return Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
}

export function minsOverdue(promisedDeliveryAt) {
  if (!promisedDeliveryAt) return 0;
  return Math.max(0, (Date.now() - new Date(promisedDeliveryAt).getTime()) / 60000);
}

export function scopeByWorkshop(rows, { scopeLocations, role, advisorName }) {
  let scoped = scopeLocations === "all" ? rows : rows.filter((v) => scopeLocations.includes(v.workshopLocation));
  if (role === "sa") scoped = scoped.filter((v) => v.advisorName === advisorName);
  return scoped;
}

// Narrows further to the single workshop the user had selected on Overview before
// drilling into one of these queues (carried over via the `?location=` query param).
// `null`/"All Locations" means no such narrowing was requested.
export function filterByLocationParam(rows, location) {
  if (!location || location === "All Locations") return rows;
  return rows.filter((v) => v.workshopLocation === location);
}

export function toQueueVehicle(row) {
  return {
    id: row._row,
    customerName: row["Customer Name"] || "—",
    vehicleReg: row["Group Name"] || "—",
    workshopLocation: row["Workshop Location"] || "—",
    advisorName: row["Service Advisor Name"] || "—",
    createdAt: row["Created At"] || null,
    promisedDeliveryAt: row["Promised Delivery At"] || null,
    slaState: String(row["Delivery SLA Status"] || "").toLowerCase(),
    lastError: row["Last Error"] || "",
    row,
  };
}

// Escalations derived live from classifier-raised flags — there is no dedicated
// `escalations` table row behind these yet, see docs/backend-integration.md item 3.
//
// Sentiment-pattern-raised incidents (3+ negative messages with no explicit flag) are
// deliberately not surfaced here — the raw sentiment_history rows are still fetched and
// still stored exactly as before, this just stops the frontend from treating that pattern
// as its own escalation on top of the classifier's flag.
export function buildEscalations(groups, sentimentRows, staffToneRows = []) {
  const byGroup = new Map();
  sentimentRows.forEach((s) => {
    if (s.group_row_id == null) return;
    if (!byGroup.has(s.group_row_id)) byGroup.set(s.group_row_id, []);
    byGroup.get(s.group_row_id).push(s);
  });
  byGroup.forEach((rows) => rows.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));

  const incidents = [];
  groups.forEach((g) => {
    if (!isEscalationFlagged(g["Escalation Flag"])) return;
    const rowId = g._row;
    const history = byGroup.get(rowId) || [];
    const flaggedRows = history.filter((h) => h.escalation_flag);
    const trigger = flaggedRows[flaggedRows.length - 1] || history[history.length - 1] || null;

    incidents.push({
      id: rowId,
      groupRowId: rowId,
      customerName: g["Customer Name"] || "—",
      vehicleReg: g["Group Name"] || "—",
      workshopLocation: g["Workshop Location"] || "—",
      advisorName: g["Service Advisor Name"] || "—",
      source: "classifier",
      reasonText: trigger?.sentiment_reason || "Flagged for review",
      triggeringMessage: trigger?.triggering_message || "",
      raisedAt: trigger?.created_at || g["Created At"] || null,
      status: "open",
      ownerName: null,
      closedAt: null,
      timeline: history.map((h) => ({ sentiment: h.sentiment, note: h.sentiment_reason, at: h.created_at })),
    });
  });

  // Staff tone flags are a separate incident source, judged independently of
  // customer sentiment — a group can carry both at once, or either alone.
  // Without this, a flag captured and even alerted on by the webhook never
  // surfaced anywhere but that one group's own Journey tab.
  const groupsByRow = new Map(groups.map((g) => [g._row, g]));
  staffToneRows.forEach((f) => {
    if (f.risk_level === "low") return;
    if (f.status !== "open") return; // reviewed = handled, drops off this list
    const g = groupsByRow.get(f.group_row_id);
    incidents.push({
      id: `staff-tone-${f.id}`,
      groupRowId: f.group_row_id,
      customerName: g?.["Customer Name"] || "—",
      vehicleReg: g?.["Group Name"] || "—",
      workshopLocation: g?.["Workshop Location"] || "—",
      advisorName: g?.["Service Advisor Name"] || "—",
      source: "staff_tone",
      reasonText: f.reason || "Staff conduct flagged for review",
      triggeringMessage: f.triggering_message || "",
      raisedAt: f.created_at,
      status: "open",
      ownerName: null,
      closedAt: null,
      timeline: [{ sentiment: f.risk_level, note: f.reason, at: f.created_at }],
    });
  });

  return incidents;
}
